import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { api } from "../lib/api";
import { useToast } from "../hooks/use-toast";
import useReveal from "../hooks/useReveal";
import {
  Activity,
  ShieldCheck,
  BookOpen,
  TrendingUp,
  Lock,
  Sparkles,
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  LineChart,
  PieChart,
  Users,
  FileText,
  Scale,
  Landmark,
  CheckCircle2,
  Menu,
  X,
  Briefcase,
  Wallet,
  Receipt,
  Target,
  ShieldQuestion,
  BarChart3,
} from "lucide-react";

// If REACT_APP_GOOGLE_CLIENT_ID is set (self-hosted deploy) we use standalone
// Google OAuth. Otherwise we fall back to Emergent Auth (preview deploy).
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

const LOGO =
  "https://customer-assets.emergentagent.com/job_admin-portal-515/artifacts/63zm8410_DAASMIN%20FINAL%20LOGO%20FOR%20TRADEMARK%20%281%29.png";

const NAV_LINKS = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Why Us", href: "#why" },
  { label: "Contact", href: "#contact" },
];

export default function Login() {
  const { user, loginWithGoogle, loading, setUser } = useAuth();
  const { toast } = useToast();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useReveal();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const useStandalone = Boolean(GOOGLE_CLIENT_ID);

  const handleGoogleCredential = async (response) => {
    try {
      const res = await api.exchangeSession(null, response.credential);
      setUser(res.user);
    } catch (err) {
      toast({
        title: "Access denied",
        description:
          err.response?.data?.detail ||
          "We could not sign you in. Please contact the admin.",
      });
    }
  };

  const SignInControl = ({ compact = false, testid }) => {
    if (useStandalone) {
      return (
        <div data-testid={testid || "signin-google-widget"} className="flex">
          <GoogleLogin
            onSuccess={handleGoogleCredential}
            onError={() =>
              toast({
                title: "Sign-in failed",
                description: "Google sign-in could not complete.",
              })
            }
            theme="filled_black"
            shape="pill"
            size={compact ? "medium" : "large"}
            text="signin_with"
          />
        </div>
      );
    }
    return (
      <Button
        onClick={loginWithGoogle}
        data-testid={testid || "signin-btn"}
        className={`group bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold gap-2 rounded-full shadow-[0_8px_28px_-8px_rgba(16,185,129,0.55)] ${
          compact ? "h-9 px-4 text-sm" : "h-12 px-6 text-[15px]"
        }`}
      >
        <GoogleIcon />
        Member Sign In
        <ArrowRight
          size={16}
          className="opacity-70 transition-transform group-hover:translate-x-0.5"
        />
      </Button>
    );
  };

  const scrollTo = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const page = (
    <div className="app-bg min-h-screen relative overflow-x-hidden text-slate-200">
      {/* Background orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
        <div
          className="absolute -top-40 -left-40 w-[38rem] h-[38rem] rounded-full bg-emerald-500/12 blur-3xl animate-pulse"
          style={{ animationDuration: "8s" }}
        />
        <div
          className="absolute top-[30%] -right-40 w-[38rem] h-[38rem] rounded-full bg-sky-500/10 blur-3xl animate-pulse"
          style={{ animationDuration: "11s" }}
        />
      </div>

      {/* ───────────── HEADER / NAV — sign-in top-right ───────────── */}
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/70 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.6)]"
            : "bg-transparent"
        }`}
        data-testid="site-header"
      >
        <nav className="max-w-7xl mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center justify-between">
          {/* Brand — left */}
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center gap-3 group"
            data-testid="brand-home-link"
          >
            <div className="relative">
              <img
                src={LOGO}
                alt="Daasmin Financials"
                className="w-10 h-10 md:w-11 md:h-11 rounded-lg bg-white p-1 ring-1 ring-emerald-500/30 relative z-10"
              />
              <div className="absolute -inset-1.5 rounded-xl bg-emerald-500/25 blur-md" aria-hidden />
            </div>
            <div className="text-left">
              <div className="font-display tracking-wide text-lg md:text-xl brand-gradient leading-none">
                DAASMIN
              </div>
              <div className="text-[9px] md:text-[10px] text-slate-500 uppercase tracking-[0.22em] font-mono mt-1">
                Financials · Family Office
              </div>
            </div>
          </button>

          {/* Links — center (desktop) */}
          <div className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                onClick={() => scrollTo(l.href)}
                data-testid={`nav-${l.href.slice(1)}`}
                className="text-sm text-slate-300 hover:text-emerald-300 transition-colors relative after:absolute after:-bottom-1.5 after:left-0 after:w-0 after:h-px after:bg-emerald-400 after:transition-all hover:after:w-full"
              >
                {l.label}
              </button>
            ))}
          </div>

          {/* Sign in — right corner */}
          <div className="flex items-center gap-3">
            <div className="hidden sm:block">
              <SignInControl compact testid="header-signin" />
            </div>
            <button
              className="lg:hidden text-slate-300 p-2"
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </nav>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="lg:hidden bg-slate-950/95 backdrop-blur-xl border-b border-slate-800/70 px-4 py-4 space-y-1" data-testid="mobile-menu">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                onClick={() => scrollTo(l.href)}
                className="block w-full text-left px-3 py-2.5 rounded-lg text-slate-300 hover:bg-slate-800/60 hover:text-emerald-300 transition-colors"
              >
                {l.label}
              </button>
            ))}
            <div className="pt-2">
              <SignInControl testid="mobile-signin" />
            </div>
          </div>
        )}
      </header>

      {/* ───────────── HERO ───────────── */}
      <section className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pt-28 md:pt-40 pb-16 md:pb-24">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/5 px-3.5 py-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-emerald-300/90">
              <span className="pulse-dot" style={{ width: 6, height: 6 }} />
              Private Family Office · Est. Trust
            </div>

            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-display leading-[1.05] tracking-tight">
              <span className="text-white">Your Family Office,</span>
              <br />
              <span className="brand-gradient">Your Road to Success.</span>
            </h1>

            <p className="mt-6 text-base md:text-lg text-slate-400 max-w-xl leading-relaxed">
              Daasmin Financials manages, grows, and safeguards family wealth with
              institution-grade transparency — daily mark-to-market NAV, per-member
              money-weighted returns, and a fully audited ledger reconciled every day.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <SignInControl testid="hero-signin" />
              <button
                onClick={() => scrollTo("#services")}
                data-testid="hero-explore-btn"
                className="group inline-flex items-center gap-2 text-sm font-medium text-slate-200 hover:text-emerald-300 transition-colors"
              >
                Explore what we do
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] font-mono text-slate-500">
              <Trust label="17 family offices" tone="emerald" />
              <Trust label="360-day NAV history" />
              <Trust label="3-tier P&L statements" />
              <Trust label="Books balanced everyday" tone="emerald" />
            </div>
          </div>

          {/* Feature cards column */}
          <div className="lg:col-span-5">
            <div className="grid gap-4">
              <FeatureCard icon={Activity} title="Live NAV" sub="Daily mark-to-market valuation across the entire book" />
              <FeatureCard icon={TrendingUp} title="Per-member XIRR" sub="True money-weighted return for every family member" />
              <FeatureCard icon={BookOpen} title="Audited Ledger" sub="Double-entry, drift-reconciled and closed daily" />
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── STATS STRIP ───────────── */}
      <section className="relative z-10 border-y border-slate-800/60 bg-slate-950/40">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { k: "17", l: "Family offices served" },
            { k: "360d", l: "NAV history tracked" },
            { k: "3-tier", l: "P&L statements" },
            { k: "100%", l: "Books reconciled daily" },
          ].map((s, i) => (
            <div key={i} className="reveal text-center md:text-left" style={{ transitionDelay: `${i * 80}ms` }}>
              <div className="text-3xl md:text-4xl font-display brand-gradient">{s.k}</div>
              <div className="mt-1 text-[11px] md:text-xs font-mono uppercase tracking-[0.14em] text-slate-500">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ───────────── ABOUT DAASMIN ───────────── */}
      <section id="about" className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28 scroll-mt-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="reveal">
            <SectionEyebrow icon={Landmark} label="About Daasmin Financials" />
            <h2 className="mt-4 text-3xl md:text-4xl font-display text-white leading-tight">
              A modern family office built on{" "}
              <span className="brand-gradient">transparency & trust.</span>
            </h2>
            <p className="mt-5 text-slate-400 leading-relaxed">
              Daasmin Financials is a private family-office platform that brings the
              discipline of institutional asset management to family wealth. We consolidate
              every rupee — equity, derivatives, cash and bonuses — into a single,
              always-current view so every member knows exactly where they stand.
            </p>
            <p className="mt-4 text-slate-400 leading-relaxed">
              No spreadsheets, no guesswork. Our books are double-entry, drift-reconciled and
              closed at the end of every trading day, giving families the same rigour a
              professional fund runs on.
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {[
                "Daily mark-to-market NAV",
                "Money-weighted per-member returns",
                "Fully audited, double-entry ledger",
                "Member-level access controls",
              ].map((t) => (
                <div key={t} className="flex items-start gap-2.5 text-sm text-slate-300">
                  <CheckCircle2 size={17} className="text-emerald-400 mt-0.5 shrink-0" />
                  {t}
                </div>
              ))}
            </div>
          </div>

          <div className="reveal" style={{ transitionDelay: "120ms" }}>
            <div className="holo-border scan-sheen bg-slate-900/60 backdrop-blur-xl rounded-2xl p-7 md:p-9">
              <div className="grid grid-cols-2 gap-5">
                <MiniStat icon={PieChart} value="Unified" label="Portfolio view" />
                <MiniStat icon={LineChart} value="Real-time" label="Valuation" />
                <MiniStat icon={Scale} value="Balanced" label="Every single day" />
                <MiniStat icon={ShieldCheck} value="Secured" label="OAuth 2.0 access" />
              </div>
              <div className="mt-6 pt-6 border-t border-slate-800/70 text-sm text-slate-400 leading-relaxed">
                “We treat every family like an institution — with the reporting, controls and
                accountability to match.”
              </div>
            </div>
          </div>
        </div>

        {/* Expanded detail — full range of Daasmin Financial Services */}
        <div className="reveal mt-14 md:mt-20">
          <div className="max-w-3xl">
            <SectionEyebrow icon={Briefcase} label="Our expertise" />
            <h3 className="mt-4 text-2xl md:text-3xl font-display text-white leading-tight">
              A full spectrum of family financial services.
            </h3>
            <p className="mt-4 text-slate-400 leading-relaxed">
              Beyond day-to-day portfolio tracking, Daasmin Financials acts as your family's
              complete financial back office — combining investment management, accounting,
              tax-aware reporting and long-term planning under one roof, so your family can
              focus on decisions instead of paperwork.
            </p>
          </div>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {ABOUT_SERVICES.map((s, i) => (
              <div
                key={s.title}
                className="holo-border bg-slate-900/50 backdrop-blur-sm rounded-2xl p-6"
                style={{ transitionDelay: `${(i % 3) * 90}ms` }}
                data-testid={`about-service-${i}`}
              >
                <div className="w-11 h-11 rounded-xl bg-emerald-500/10 ring-1 ring-emerald-500/30 flex items-center justify-center">
                  <s.icon size={19} className="text-emerald-300" />
                </div>
                <h4 className="mt-4 text-base font-display text-white">{s.title}</h4>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── SERVICES ───────────── */}
      <section id="services" className="relative z-10 border-y border-slate-800/60 bg-slate-950/40 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
          <div className="reveal max-w-2xl">
            <SectionEyebrow icon={Sparkles} label="What we do" />
            <h2 className="mt-4 text-3xl md:text-4xl font-display text-white leading-tight">
              Everything a family needs to run its wealth like a fund.
            </h2>
            <p className="mt-4 text-slate-400 leading-relaxed">
              A complete operating system for family capital — from valuation to reporting to
              governance.
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => (
              <div
                key={s.title}
                className="reveal holo-border scan-sheen bg-slate-900/55 backdrop-blur-sm rounded-2xl p-6 transition-transform hover:-translate-y-1"
                style={{ transitionDelay: `${(i % 3) * 90}ms` }}
                data-testid={`service-card-${i}`}
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-slate-800 to-slate-950 ring-1 ring-emerald-500/30 flex items-center justify-center">
                  <s.icon size={20} className="text-emerald-300" />
                </div>
                <h3 className="mt-5 text-lg font-display text-white">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── WHY DAASMIN ───────────── */}
      <section id="why" className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28 scroll-mt-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="reveal order-2 lg:order-1">
            <div className="grid gap-4">
              {WHY.map((w, i) => (
                <div
                  key={w.title}
                  className="flex items-start gap-4 rounded-xl border border-slate-800/70 bg-slate-900/40 p-5"
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 ring-1 ring-emerald-500/30 flex items-center justify-center shrink-0">
                    <w.icon size={18} className="text-emerald-300" />
                  </div>
                  <div>
                    <div className="text-white font-semibold">{w.title}</div>
                    <div className="mt-1 text-sm text-slate-400 leading-relaxed">{w.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="reveal order-1 lg:order-2">
            <SectionEyebrow icon={ShieldCheck} label="Why Daasmin" />
            <h2 className="mt-4 text-3xl md:text-4xl font-display text-white leading-tight">
              Institutional rigour, <span className="brand-gradient">family intimacy.</span>
            </h2>
            <p className="mt-5 text-slate-400 leading-relaxed">
              Families choose Daasmin because we combine the controls of a professional fund
              with the care and discretion of a private office. Every member is
              pre-authorized individually, every transaction is logged, and every number ties
              back to a reconciled ledger.
            </p>
            <div className="mt-8">
              <SignInControl testid="why-signin" />
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── CONTACT ───────────── */}
      <section id="contact" className="relative z-10 border-t border-slate-800/60 bg-slate-950/50 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            <div className="reveal">
              <SectionEyebrow icon={Mail} label="Contact us" />
              <h2 className="mt-4 text-3xl md:text-4xl font-display text-white leading-tight">
                Talk to the Daasmin team.
              </h2>
              <p className="mt-4 text-slate-400 leading-relaxed max-w-lg">
                Not yet on the authorized member list, or want to bring your family onto
                Daasmin? Reach out — we respond within one business day.
              </p>

              <div className="mt-8 space-y-4">
                <ContactRow
                  icon={Phone}
                  label="Phone"
                  value="+91 93726 06001"
                  href="tel:+919372606001"
                  testid="contact-phone"
                />
                <ContactRow
                  icon={Mail}
                  label="Email"
                  value="support@daasmin.in"
                  href="mailto:support@daasmin.in"
                  testid="contact-email"
                />
                <ContactRow
                  icon={MapPin}
                  label="Office"
                  value="Daasmin Financials · India"
                  testid="contact-address"
                />
              </div>
            </div>

            <div className="reveal" style={{ transitionDelay: "120ms" }}>
              <div className="holo-border bg-slate-900/60 backdrop-blur-xl rounded-2xl p-7 md:p-9">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={14} className="text-emerald-300" />
                  <span className="text-[10px] uppercase tracking-[0.22em] text-emerald-300/90 font-mono">
                    Authorized access
                  </span>
                </div>
                <h3 className="mt-4 text-2xl font-display text-white">Member sign in</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">
                  Use your authorized Gmail to enter the portal. The admin pre-authorizes every
                  family member individually.
                </p>
                <div className="mt-6 flex" data-testid="contact-signin-wrapper">
                  <SignInControl testid="contact-signin" />
                </div>
                <div className="mt-7 pt-5 border-t border-slate-800/70 grid grid-cols-2 gap-3 text-[11px] font-mono">
                  <StatLine icon={Lock} label="OAuth 2.0" tone="emerald" />
                  <StatLine icon={Sparkles} label="Member-aware" tone="amber" />
                  <StatLine icon={ShieldCheck} label="No password" tone="emerald" />
                  <StatLine icon={BookOpen} label="Audit logged" tone="sky" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── FOOTER ───────────── */}
      <footer className="relative z-10 border-t border-slate-800/70">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="Daasmin" className="w-9 h-9 rounded-lg bg-white p-1 ring-1 ring-emerald-500/30" />
            <div>
              <div className="font-display brand-gradient text-lg leading-none">DAASMIN</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-[0.22em] font-mono mt-1">
                Financials · Family Office
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                onClick={() => scrollTo(l.href)}
                className="text-slate-400 hover:text-emerald-300 transition-colors"
              >
                {l.label}
              </button>
            ))}
            <a href="mailto:support@daasmin.in" className="text-slate-400 hover:text-emerald-300 transition-colors">
              support@daasmin.in
            </a>
          </div>

          <div className="text-[10px] text-slate-600 font-mono uppercase tracking-[0.22em] text-center">
            © {new Date().getFullYear()} Daasmin Financials · Confidential
          </div>
        </div>
      </footer>
    </div>
  );

  if (useStandalone) {
    return <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>{page}</GoogleOAuthProvider>;
  }
  return page;
}

/* ───────────────────────── data ───────────────────────── */

const ABOUT_SERVICES = [
  { icon: Briefcase, title: "Investment Management", desc: "Active management of equity and derivative portfolios with a disciplined, risk-first mandate for every family." },
  { icon: Wallet, title: "Wealth Consolidation", desc: "All family assets — investments, cash and bonuses — unified into one clear, always-current statement." },
  { icon: Receipt, title: "Accounting & Reconciliation", desc: "Double-entry bookkeeping with daily drift reconciliation so every number is verified, not estimated." },
  { icon: BarChart3, title: "Performance Reporting", desc: "Per-member XIRR, NAV history and 3-tier P&L statements delivered with institutional clarity." },
  { icon: Target, title: "Financial Planning", desc: "Goal-based planning and capital allocation tailored to each family's horizon and risk appetite." },
  { icon: ShieldQuestion, title: "Governance & Advisory", desc: "Member-level access controls, audit trails and ongoing advisory to keep the family aligned." },
];

const SERVICES = [
  { icon: LineChart, title: "Portfolio Valuation", desc: "Daily mark-to-market NAV across equity, derivatives and cash — always current." },
  { icon: TrendingUp, title: "Per-member Returns", desc: "Money-weighted XIRR computed for every member from their real cashflows." },
  { icon: BookOpen, title: "Audited Ledger", desc: "Double-entry books, drift-reconciled and closed at the end of every trading day." },
  { icon: FileText, title: "P&L Statements", desc: "Three-tier profit & loss reporting with equity, derivative and bonus breakdowns." },
  { icon: Users, title: "Family Governance", desc: "Individual member accounts, roles and pre-authorized access to sensitive data." },
  { icon: ShieldCheck, title: "Secure Access", desc: "Google OAuth 2.0 sign-in, passwordless, with a full audit log of every action." },
];

const WHY = [
  { icon: Scale, title: "Books balanced every day", desc: "No month-end surprises — reconciliation happens daily, not quarterly." },
  { icon: PieChart, title: "One unified view", desc: "Equity, derivatives, cash and bonuses consolidated into a single source of truth." },
  { icon: Lock, title: "Member-level controls", desc: "Each family member is authorized individually with role-based visibility." },
  { icon: Activity, title: "Live, not lagging", desc: "Valuations update with the market so decisions are made on today's numbers." },
];

/* ───────────────────────── building blocks ───────────────────────── */

function FeatureCard({ icon: Icon, title, sub }) {
  return (
    <div className="holo-border scan-sheen bg-slate-900/55 backdrop-blur-sm rounded-xl px-5 py-4 flex items-start gap-3.5 transition-transform hover:-translate-y-0.5">
      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-800 to-slate-950 ring-1 ring-emerald-500/30 flex items-center justify-center shrink-0">
        <Icon size={18} className="text-emerald-300" />
      </div>
      <div className="min-w-0">
        <div className="text-sm text-white font-semibold tracking-tight">{title}</div>
        <div className="text-xs text-slate-400 leading-relaxed mt-1">{sub}</div>
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, value, label }) {
  return (
    <div className="rounded-xl bg-slate-950/50 border border-slate-800/70 p-4">
      <Icon size={18} className="text-emerald-300" />
      <div className="mt-3 text-white font-display text-lg">{value}</div>
      <div className="text-[11px] font-mono uppercase tracking-[0.12em] text-slate-500 mt-0.5">
        {label}
      </div>
    </div>
  );
}

function SectionEyebrow({ icon: Icon, label }) {
  return (
    <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-emerald-300/90">
      <Icon size={13} />
      {label}
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, href, testid }) {
  const inner = (
    <div className="flex items-center gap-4 rounded-xl border border-slate-800/70 bg-slate-900/40 px-5 py-4 transition-colors hover:border-emerald-500/40 hover:bg-slate-900/60">
      <div className="w-11 h-11 rounded-lg bg-emerald-500/10 ring-1 ring-emerald-500/30 flex items-center justify-center shrink-0">
        <Icon size={18} className="text-emerald-300" />
      </div>
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">{label}</div>
        <div className="text-white font-medium mt-0.5">{value}</div>
      </div>
    </div>
  );
  if (href) {
    return (
      <a href={href} data-testid={testid} className="block">
        {inner}
      </a>
    );
  }
  return (
    <div data-testid={testid}>{inner}</div>
  );
}

function Trust({ label, tone }) {
  const dot = tone === "emerald" ? "bg-emerald-400" : "bg-slate-500";
  return (
    <div className="flex items-center gap-1.5">
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      <span className="uppercase tracking-[0.14em]">{label}</span>
    </div>
  );
}

function StatLine({ icon: Icon, label, tone }) {
  const toneCls = {
    emerald: "text-emerald-300 border-emerald-500/30 bg-emerald-500/5",
    amber: "text-amber-300 border-amber-500/30 bg-amber-500/5",
    sky: "text-sky-300 border-sky-500/30 bg-sky-500/5",
  }[tone || "emerald"];
  return (
    <div className={`rounded-md border ${toneCls} px-2 py-1.5 flex items-center gap-1.5`}>
      <Icon size={11} />
      <span className="text-[10px] uppercase tracking-[0.12em] truncate">{label}</span>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
      <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
      <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
    </svg>
  );
}
